import { FormControl, FormLabel, NumberInput, NumberInputField } from '@chakra-ui/react';
import type { NumberInputProps } from '@chakra-ui/react';
import { FormFieldMeta } from '@repo/shared/shared';
import { useTranslation } from 'next-i18next';
import { FC } from 'react';
import { useController } from 'react-hook-form';
import { getValidationMessages, phoneRegExp, phoneRegExpWithSpaces } from '@/shared/constants';
import { phoneFormat } from '@/shared/helpers';

interface PhoneFieldProps extends NumberInputProps {
  validationParameters?: object;
}

const PhoneField: FC<PhoneFieldProps> = ({
  name = 'phone',
  onChange,
  onBlur,
  validationParameters,
  ...rest
}) => {
  const { t } = useTranslation();
  const messages = getValidationMessages(t);

  const {
    fieldState,
    field: { onChange: onFieldChange, onBlur: onFieldBlur, value },
  } = useController({
    name,
    rules: {
      pattern: {
        value: new RegExp(phoneRegExp),
        message: messages.invalidPhone(),
      },
      ...validationParameters,
    },
  });
  const { invalid, error } = fieldState;

  const handleChange: NumberInputProps['onChange'] = (stringValue, numberValue) => {
    onFieldChange(stringValue);
    onChange?.(stringValue, numberValue);
  };

  const handleBlur: NumberInputProps['onBlur'] = (e) => {
    onFieldBlur();
    onBlur?.(e);
  };

  return (
    <FormControl isInvalid={invalid}>
      <FormLabel
        color='primary.pure'
        mb='1'
      >
        {t('common.form.phone')}
      </FormLabel>
      <NumberInput
        inputMode='tel'
        pattern={phoneRegExpWithSpaces}
        value={value && phoneFormat(value.toString())}
        onBlur={handleBlur}
        onChange={handleChange}
        {...rest}
      >
        <NumberInputField
          id='phone'
          placeholder={t('common.form.phonePlaceholder')}
        />
      </NumberInput>
      <FormFieldMeta
        errors={{ [name]: error }}
        name={name}
      />
    </FormControl>
  );
};

export default PhoneField;
